/**
 * ═══════════════════════════════════════════════════════════════════════
 * useRealtimeSync — Main Hook to Initialize Realtime Sync (Hardened)
 * ═══════════════════════════════════════════════════════════════════════
 *
 * SECURITY:
 *   • Tenant ID validated from Cognito session (custom:org_id claim),
 *     NOT blindly trusted from localStorage (user-writable via DevTools).
 *   • Auth token provided via lazy provider function — always fresh on
 *     each WebSocket connect/reconnect. Never stale.
 *   • Polling uses httpOnly cookies (withCredentials) — no token in URL.
 *
 * Call this ONCE at the app shell level (e.g., AdminDashboard).
 *
 * Usage:
 *   function AppShell() {
 *     const { connectionStatus } = useRealtimeSync();
 *     return <div>Status: {connectionStatus}</div>;
 *   }
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { tenantChannel } from '../services/realtime';
import { REALTIME_EVENTS } from '../services/realtime/RealtimeSyncService';
import eventBus from '../services/realtime/EventBus';
import { fetchDailyVisits, fetchDoctors } from '../services/api/visitApi';
import { STORAGE_KEYS } from '../constants';
import useVisitStore from '../stores/visitStore';
import { getAuthToken } from '../services/api/apiClient';

/**
 * Provides a FRESH JWT token on every call.
 * Called by RealtimeSyncService on each WebSocket connect/reconnect.
 *
 * Strategy:
 *   1. Try Amplify's fetchAuthSession (works if user signed in via Amplify)
 *   2. Fall back to sessionStorage token from apiClient (custom auth flow)
 *
 * @returns {Promise<string|null>} Fresh JWT ID token or null
 */
async function getSecureAuthToken() {
  // 1. Try Amplify session (native Amplify signIn flow)
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    const amplifyToken = session?.tokens?.idToken?.toString();
    if (amplifyToken) return amplifyToken;
  } catch {
    // Amplify session not available — expected for custom auth flow
  }

  // 2. Fall back to in-memory / sessionStorage token (custom backend auth)
  const storedToken = getAuthToken();
  if (storedToken) {
    console.debug('[RT] Using sessionStorage token for WS auth');
    return storedToken;
  }

  console.warn('[RT] No auth token available from any source');
  return null;
}

/**
 * Validate tenant identity from Cognito session claims.
 * Compares the org_id stored in the JWT (server-issued, tamper-proof)
 * against what's in localStorage.
 *
 * @returns {string|null} Validated tenant ID or null if mismatch/missing
 */
async function getValidatedTenantId() {
  const storedTenantId = localStorage.getItem(STORAGE_KEYS.ORGANIZATION_ID);
  if (!storedTenantId) return null;

  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    const claims = session?.tokens?.idToken?.payload;

    // Cognito custom attribute — the authoritative tenant identity
    const tokenOrgId = claims?.['custom:org_id'] || claims?.['custom:organization_id'];

    if (tokenOrgId && tokenOrgId !== storedTenantId) {
      console.warn(
        '[RealtimeSync] Tenant ID mismatch: localStorage=', storedTenantId,
        'JWT=', tokenOrgId, '— using JWT value'
      );
      return tokenOrgId; // Trust the JWT, not localStorage
    }

    // If no org claim in token (individual user), use localStorage as fallback
    return storedTenantId;
  } catch {
    // Can't validate — use localStorage cautiously
    return storedTenantId;
  }
}

/**
 * @returns {{ connectionStatus: string, isConnected: boolean, syncMode: string|null }}
 */
export function useRealtimeSync() {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [syncMode, setSyncMode] = useState(null);
  const cleanupRef = useRef(null);
  const scopeDoctorId = useVisitStore((s) => s.scope.doctorId);

  // Keep a ref to scope so the connect call always has the latest
  const scopeRef = useRef({ doctorId: scopeDoctorId });
  scopeRef.current = { doctorId: scopeDoctorId };

  // Stable poll fetcher — reads scope from ref so the callback identity
  // never changes (prevents useEffect re-fires → disconnect/reconnect cycles)
  const pollFetcher = useCallback(async () => {
    try {
      const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
      const filters = { date: today };

      // Auto-inject doctor scope for scoped roles (from ref, not closure)
      if (scopeRef.current.doctorId) {
        filters.doctor_id = scopeRef.current.doctorId;
      }

      // Fetch visits + doctors in parallel for complete reconciliation
      const [visitsData, doctorsData] = await Promise.all([
        fetchDailyVisits(filters),
        fetchDoctors().catch(() => null),
      ]);

      return {
        dailyVisits: visitsData.visits || [],
        doctors: doctorsData?.doctors || null,
      };
    } catch {
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initSync() {
      // Validate tenant from auth session, not just localStorage
      const tenantId = await getValidatedTenantId();
      if (process.env.REACT_APP_DEBUG === 'true') {
        console.log('[RT_DEBUG] useRealtimeSync init:', { tenantId: tenantId ? '✓' : '✗ NULL', cancelled });
      }
      if (!tenantId || cancelled) return;

      // Connect with a token PROVIDER (not a static token)
      tenantChannel.connect({
        tenantId,
        authTokenProvider: getSecureAuthToken,
        pollFetcher,
        scope: scopeRef.current,
      });

      // Listen for connection lifecycle events
      const unsubConnected = eventBus.on(REALTIME_EVENTS.CONNECTED, (event) => {
        if (!cancelled) {
          setConnectionStatus('connected');
          setSyncMode(event.payload?.mode || null);
        }
      });

      const unsubDisconnected = eventBus.on(REALTIME_EVENTS.DISCONNECTED, () => {
        if (!cancelled) {
          setConnectionStatus('disconnected');
          setSyncMode(null);
        }
      });

      const unsubReconnecting = eventBus.on(REALTIME_EVENTS.RECONNECTING, (event) => {
        if (!cancelled) {
          setConnectionStatus(`reconnecting (attempt ${event.payload?.attempt})`);
        }
      });

      cleanupRef.current = () => {
        unsubConnected();
        unsubDisconnected();
        unsubReconnecting();
        tenantChannel.disconnect();
      };
    }

    initSync();

    return () => {
      cancelled = true;
      cleanupRef.current?.();
    };
  }, [pollFetcher]); // stable — only runs once on mount

  return {
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    syncMode,
  };
}

export default useRealtimeSync;
